import { CardShell, EmptyState, ListSkeleton } from '@atlas/ui-components'
import { fileTypeToken, formatBytes } from '@atlas/ui-domain'
import { Button, cn, Tag } from '@atlas/ui-primitives'
import type {
	CampaignDownloadAvailability,
	CampaignDownloadCapabilityState,
	CampaignFile,
} from '@/domain/campaign.types'
import { isDistributionPackageFile } from '@/lib/campaign-download-capabilities'
import { CampaignDownloadMenu } from './CampaignDownloadMenu'
import { CampaignFilePreview } from './CampaignFilePreview'

export interface CampaignFileGalleryProps {
	files: readonly CampaignFile[]
	isLoading: boolean
	isError: boolean
	selectedFileIds: ReadonlySet<string>
	onToggleSelection: (fileId: string) => void
	downloadCapabilities?: CampaignDownloadCapabilityState
	onDownloadIndividual: (fileId: string) => void
	/** The one File whose download URL request is in flight — only its own control disables. */
	downloadingFileId?: string | null
	/**
	 * Resolved thumbnail URLs by File id, supplied by the container that consumed
	 * the thumbnail-resolution capability. Omitted entries fall back to the
	 * File-type token, so a caller that supplies nothing still renders correctly.
	 */
	thumbnailUrls?: Readonly<Record<string, string | null>>
	canAssociateFiles: boolean
	canUploadFiles: boolean
	onBrowseExisting: () => void
	onUploadNew: () => void
	/** Bulk-download actions — the gallery-level aggregate `CampaignDownloadMenu` instance's callbacks and shared pending/error state. */
	onDownloadSelected?: () => void
	onDownloadAll?: () => void
	isBulkDownloadPending?: boolean
	bulkDownloadError?: string
}

/**
 * The visible size for a card, or `null` when no size can be represented.
 * `formatBytes` documents `-` as its unknown marker and handles null/NaN/negative;
 * a non-finite value is screened here so it never reaches the formatter. A real
 * `0` is a valid size and renders as `0 B` — never treated as absent.
 */
function presentableSize(fileSize: number | null): string | null {
	if (fileSize === null || !Number.isFinite(fileSize)) return null
	const formatted = formatBytes(fileSize)
	return formatted === '-' ? null : formatted
}

/** One associated-File card: preview, selection checkbox, metadata, and that File's own download control. */
function CampaignFileCard({
	file,
	selected,
	onToggleSelection,
	availability,
	onDownloadIndividual,
	thumbnailUrl,
}: {
	file: CampaignFile
	selected: boolean
	onToggleSelection: (fileId: string) => void
	availability: CampaignDownloadAvailability
	onDownloadIndividual: (fileId: string) => void
	thumbnailUrl: string | null
}) {
	const distributionPackage = isDistributionPackageFile(file)
	const typeLabel = fileTypeToken(file.contentType, file.fileName)
	const sizeLabel = presentableSize(file.fileSize)

	return (
		// Selection is exposed programmatically by the checkbox below; `data-selected`
		// carries only the visual treatment, since this root has no role for which
		// `aria-selected` would be valid.
		<CardShell
			data-selected={selected}
			className={cn(
				'overflow-hidden p-0',
				'data-[selected=true]:ring-2 data-[selected=true]:ring-ds-primary',
				distributionPackage ? 'border-ds-primary' : '',
			)}
		>
			<CampaignFilePreview fileName={file.fileName} contentType={file.contentType} thumbnailUrl={thumbnailUrl} />
			<div className="space-y-1 border-t border-ds-border p-2">
				<label className="flex items-start gap-2 text-sm text-ds-text">
					<input
						type="checkbox"
						checked={selected}
						onChange={() => onToggleSelection(file.fileId)}
						aria-label={`Select ${file.fileName}`}
					/>
					<span className="min-w-0 truncate font-medium" title={file.fileName}>
						{file.fileName}
					</span>
				</label>
				<p className="min-w-0 truncate text-xs text-ds-text-muted">
					{sizeLabel === null ? typeLabel : `${typeLabel} · ${sizeLabel}`}
				</p>
				{distributionPackage && (
					<Tag variant="primary" size="sm">
						Distribution package
					</Tag>
				)}
				<CampaignDownloadMenu
					context="individual"
					file={file}
					availability={availability}
					onDownload={onDownloadIndividual}
				/>
			</div>
		</CardShell>
	)
}

/**
 * One distribution-package File, presented as a full-width row above the regular
 * grid. It stays an ordinary associated File: the same selection control with its
 * filename-bearing name, the same preview area with its token and failure
 * fallbacks, the same metadata, and download only through its own per-File menu
 * instance. No package is generated, aggregated, or archived here.
 */
function CampaignFilePackageRow({
	file,
	selected,
	onToggleSelection,
	availability,
	onDownloadIndividual,
	thumbnailUrl,
}: {
	file: CampaignFile
	selected: boolean
	onToggleSelection: (fileId: string) => void
	availability: CampaignDownloadAvailability
	onDownloadIndividual: (fileId: string) => void
	thumbnailUrl: string | null
}) {
	const typeLabel = fileTypeToken(file.contentType, file.fileName)
	const sizeLabel = presentableSize(file.fileSize)

	return (
		// Selection is exposed programmatically by the checkbox below; `data-selected`
		// carries only the visual treatment, since this root has no role for which
		// `aria-selected` would be valid.
		<CardShell
			data-selected={selected}
			className={cn(
				'flex items-center gap-2 overflow-hidden border-ds-primary p-2',
				'data-[selected=true]:ring-2 data-[selected=true]:ring-ds-primary',
			)}
		>
			<div className="w-24 shrink-0">
				<CampaignFilePreview fileName={file.fileName} contentType={file.contentType} thumbnailUrl={thumbnailUrl} />
			</div>
			<div className="min-w-0 flex-1 space-y-1">
				<label className="flex items-start gap-2 text-sm text-ds-text">
					<input
						type="checkbox"
						checked={selected}
						onChange={() => onToggleSelection(file.fileId)}
						aria-label={`Select ${file.fileName}`}
					/>
					<span className="min-w-0 truncate font-medium" title={file.fileName}>
						{file.fileName}
					</span>
				</label>
				<p className="min-w-0 truncate text-xs text-ds-text-muted">
					{sizeLabel === null ? typeLabel : `${typeLabel} · ${sizeLabel}`}
				</p>
				<Tag variant="primary" size="sm">
					Distribution package
				</Tag>
			</div>
			<div className="shrink-0">
				<CampaignDownloadMenu
					context="individual"
					file={file}
					availability={availability}
					onDownload={onDownloadIndividual}
				/>
			</div>
		</CardShell>
	)
}

/**
 * A File's own individual-download availability: only the File whose URL request
 * is in flight loses its control, so every other File's download stays
 * independently clickable. Shared by both File presentations so the two cannot
 * diverge.
 */
function individualAvailability(
	capabilities: CampaignDownloadCapabilityState | undefined,
	downloadingFileId: string | null | undefined,
	fileId: string,
): CampaignDownloadAvailability {
	return capabilities?.individual === 'available' && downloadingFileId !== fileId ? 'available' : 'unavailable'
}

/** The Browse/Upload entry actions, visually separated from the selection/download cluster beside it. */
function AssetEntryActions({
	canAssociateFiles,
	canUploadFiles,
	onBrowseExisting,
	onUploadNew,
}: {
	canAssociateFiles: boolean
	canUploadFiles: boolean
	onBrowseExisting: () => void
	onUploadNew: () => void
}) {
	if (!canAssociateFiles && !canUploadFiles) return null

	return (
		<div className="flex flex-wrap items-center gap-2 border-l border-ds-border pl-3">
			{canAssociateFiles && (
				<Button variant="outline" size="sm" onPress={onBrowseExisting}>
					Browse existing Files
				</Button>
			)}
			{canUploadFiles && (
				<Button variant="outline" size="sm" onPress={onUploadNew}>
					Upload new File
				</Button>
			)}
		</div>
	)
}

/**
 * The associated-Files gallery. Owns associated-File presentation, selected
 * File IDs, selection state, and the selection count. Composes
 * `CampaignDownloadMenu` once per File presentation (that File's
 * individual-download target) plus one single gallery-level instance for the
 * aggregate controls — the only component type rendering any download control.
 *
 * The section heading states the count of every associated File, distribution
 * packages included; the decorative mockup placeholders are not Files and are not
 * counted. Files are partitioned into distribution packages and regular Files by
 * the shared predicate, with the two sets complementary so every File renders
 * exactly once — zero, one, or several packages are all safe.
 */
export function CampaignFileGallery({
	files,
	isLoading,
	isError,
	selectedFileIds,
	onToggleSelection,
	downloadCapabilities,
	onDownloadIndividual,
	downloadingFileId,
	thumbnailUrls,
	canAssociateFiles,
	canUploadFiles,
	onBrowseExisting,
	onUploadNew,
	onDownloadSelected,
	onDownloadAll,
	isBulkDownloadPending,
	bulkDownloadError,
}: CampaignFileGalleryProps) {
	// Two complementary filters over one predicate, so the union is always exactly
	// `files`: no File is dropped and none is presented twice. Nothing assumes there
	// is at most one distribution package.
	const packageFiles = files.filter(isDistributionPackageFile)
	const regularFiles = files.filter((file) => !isDistributionPackageFile(file))
	const showFiles = !isLoading && !isError && files.length > 0

	return (
		<section aria-label="Files" className="space-y-5">
			<div className="flex flex-wrap items-center justify-between gap-3">
				<h2 className="text-sm font-semibold text-ds-text">{`All assets in this campaign · ${files.length}`}</h2>
				<div className="flex flex-wrap items-center gap-3">
					<div className="flex flex-wrap items-center gap-2">
						<span className="text-sm text-ds-text-muted">
							{selectedFileIds.size > 0 ? `${selectedFileIds.size} selected` : 'No Files selected'}
						</span>
						<CampaignDownloadMenu
							context="aggregate"
							selectionCount={selectedFileIds.size}
							selectedAvailability={downloadCapabilities?.selected ?? 'unavailable'}
							allAvailability={downloadCapabilities?.all ?? 'unavailable'}
							onDownloadSelected={onDownloadSelected}
							onDownloadAll={onDownloadAll}
							isPending={isBulkDownloadPending}
							errorMessage={bulkDownloadError}
						/>
					</div>
					<AssetEntryActions
						canAssociateFiles={canAssociateFiles}
						canUploadFiles={canUploadFiles}
						onBrowseExisting={onBrowseExisting}
						onUploadNew={onUploadNew}
					/>
				</div>
			</div>

			{isLoading && <ListSkeleton rows={3} label="Loading Files" />}
			{isError && (
				<p role="alert" className="text-sm text-ds-danger">
					Unable to load this Campaign's Files.
				</p>
			)}
			{!isLoading && !isError && files.length === 0 && (
				<EmptyState title="No Files are associated with this Campaign yet." />
			)}

			{showFiles && packageFiles.length > 0 && (
				<div className="space-y-3">
					{packageFiles.map((file) => (
						<CampaignFilePackageRow
							key={file.fileId}
							file={file}
							selected={selectedFileIds.has(file.fileId)}
							onToggleSelection={onToggleSelection}
							availability={individualAvailability(downloadCapabilities, downloadingFileId, file.fileId)}
							onDownloadIndividual={onDownloadIndividual}
							thumbnailUrl={thumbnailUrls?.[file.fileId] ?? null}
						/>
					))}
				</div>
			)}

			{showFiles && regularFiles.length > 0 && (
				<div className="grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6">
					{regularFiles.map((file) => (
						<CampaignFileCard
							key={file.fileId}
							file={file}
							selected={selectedFileIds.has(file.fileId)}
							onToggleSelection={onToggleSelection}
							availability={individualAvailability(downloadCapabilities, downloadingFileId, file.fileId)}
							onDownloadIndividual={onDownloadIndividual}
							thumbnailUrl={thumbnailUrls?.[file.fileId] ?? null}
						/>
					))}
				</div>
			)}
		</section>
	)
}
